// ── TRACKER PAGE (fp-nofodesk-9) — extracted from App.js ──
import { useState, useEffect, useMemo } from "react";
import { Clock } from "lucide-react";
import { TOKENS, BADGE, BADGE_BASE } from "../tokens";
import { FONT_H, FONT_UI, FONT_MONO, useT } from "../theme";
import GrantList from "../components/GrantList";
import ImportModal from "../components/ImportModal";
import { GatedButton } from "../components/PlanGate";
import { GrantFiles, DriveFolderButton } from "../components/DriveIntegration";
import { useWorkspace } from "../context/WorkspaceContext";
import { useWindowWidth } from "../hooks/useWindowWidth";
import { useDebounce } from "../hooks/useDebounce";
import { exportGOSExcel } from "../utils/exportExcel";
import { API, apiFetch, apiLoadTable, apiSyncTable, persistRecordsDiff, generateId, getToken } from "../utils/api";
import { parseAmt, parseUSD, formatUSD, ACTIVE_COLS, APP_COLS, TERMINATED_COLS, COMPLETED_COLS, daysUntilDeadline } from "../utils/grants";

import { FormCtx, SearchInput, FormField, NOASection } from "../components/TrackerForms";

// ── DeadlineDashboard ─────────────────────────────────────────
function DeadlineDashboard({ defaultSubTab, defaultPortFilter, defaultAppFilter, authUser, onNavCountsChange }) {
  const T = useT();
  const GARAMOND  = FONT_H;
  const MONO      = FONT_MONO;
  const isMobile  = useWindowWidth() < 768;
  const { canEdit } = useWorkspace();

  const [subTab,       setSubTab]      = useState(defaultSubTab || "active");
  useEffect(() => { if (defaultSubTab) setSubTab(defaultSubTab); }, [defaultSubTab]);
  const [portFilter,   setPortFilter]  = useState(defaultPortFilter || "all");
  useEffect(() => { if (defaultPortFilter) setPortFilter(defaultPortFilter); }, [defaultPortFilter]);
  const [portSearch,   setPortSearch]  = useState("");
  const [portSort,     setPortSort]    = useState("end");
  const [portPOC,      setPortPOC]     = useState("all");
  const [appFilter,    setAppFilter]   = useState(defaultAppFilter || "all");
  useEffect(() => { if (defaultAppFilter) setAppFilter(defaultAppFilter); }, [defaultAppFilter]);
  const [appSearch,    setAppSearch]   = useState("");
  const [selectedFunder, setSelectedFunder] = useState(null);
  const debouncedPortSearch = useDebounce(portSearch, 300);
  const debouncedAppSearch  = useDebounce(appSearch,  300);
  const [tracked,      setTracked]     = useState([]);

  useEffect(() => {
    if (!authUser) return;
    apiLoadTable('tracked-grants').then(data => { if (data) setTracked(data); });
  }, [authUser?.id ?? authUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistTracked = (data) => { setTracked(data); apiSyncTable('tracked-grants', data); };

  const [activeGrants,      setActiveGrantsState]     = useState([]);
  const [applications,      setApplicationsState]     = useState([]);
  const [terminatedGrants,  setTerminatedGrantsState] = useState([]);
  const [completedGrants,   setCompletedGrantsState]  = useState([]);
  const [dbLoading, setDbLoading] = useState(!!getToken());

  useEffect(() => {
    if (authUser === null) return;
    if (authUser === false) { setDbLoading(false); return; }
    setDbLoading(true);
    Promise.all([
      apiLoadTable('active-grants'),
      apiLoadTable('pipeline-apps'),
      apiLoadTable('terminated-grants'),
      apiLoadTable('completed-grants'),
    ]).then(([active, apps, terminated, completed]) => {
      if (active)     setActiveGrantsState(active);
      if (apps)       setApplicationsState(apps);
      if (terminated) setTerminatedGrantsState(terminated);
      if (completed)  setCompletedGrantsState(completed);
    }).finally(() => setDbLoading(false));
  }, [authUser?.id ?? authUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!onNavCountsChange) return;
    onNavCountsChange({
      active: activeGrants.filter(g => !g.archived).length,
      apps:   applications.filter(a => a.status !== "archived").length,
    });
  }, [activeGrants, applications, onNavCountsChange]);

  const [detailItem,        setDetailItem]   = useState(null);
  const [detailType,        setDetailType]   = useState(null);
  const [editItem,          setEditItem]     = useState(null);
  const [editType,          setEditType]     = useState(null);
  const [deleteTarget,      setDeleteTarget] = useState(null);
  const [formData,          setFormData]     = useState({});
  const [moveMenuId,        setMoveMenuId]   = useState(null);
  const [menuPos,           setMenuPos]      = useState({ top: 0, right: 0 });
  const [noteInput,         setNoteInput]    = useState("");
  const [showImport,        setShowImport]        = useState(false);
  const [importDefaultTable, setImportDefaultTable] = useState('pipeline-apps');
  const [recycleBin, setRecycleBin] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nofodesk_recycle_v1") || "[]"); } catch { return []; }
  });
  useEffect(() => { localStorage.setItem("nofodesk_recycle_v1", JSON.stringify(recycleBin)); }, [recycleBin]);
  useEffect(() => {
    const cutoff = Date.now() - 90 * 24 * 60 * 60 * 1000;
    setRecycleBin(prev => prev.filter(it => new Date(it._deleted_at).getTime() > cutoff));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-backed recycle bin (primary source of truth)
  const [binItems, setBinItems] = useState([]);
  const [binLoading, setBinLoading] = useState(false);
  useEffect(() => {
    if (subTab !== 'recyclebin' || !getToken()) return;
    setBinLoading(true);
    apiFetch(`${API}/recycle`)
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => setBinItems(d.items || []))
      .catch(() => {})
      .finally(() => setBinLoading(false));
  }, [subTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const [versionHistory, setVersionHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nofodesk_versions_v1") || "{}"); } catch { return {}; }
  });
  const [historyTarget, setHistoryTarget] = useState(null); // { id, type, name }
  const [fetchedVersions, setFetchedVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  useEffect(() => { localStorage.setItem("nofodesk_versions_v1", JSON.stringify(versionHistory)); }, [versionHistory]);
  useEffect(() => {
    if (!historyTarget || !getToken()) { setFetchedVersions([]); return; }
    const tableMap = { active: 'active_grants', app: 'pipeline_apps' };
    const dbTable = tableMap[historyTarget.type];
    if (!dbTable) return;
    setVersionsLoading(true);
    apiFetch(`${API}/versions/${dbTable}/${historyTarget.id}`)
      .then(r => r.ok ? r.json() : { versions: [] })
      .then(d => setFetchedVersions(d.versions || []))
      .catch(() => setFetchedVersions([]))
      .finally(() => setVersionsLoading(false));
  }, [historyTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveVersion = (record, type) => {
    if (!record?.id) return;
    const entry = {
      snapshot: { ...record },
      by: authUser?.name || authUser?.email || "Unknown",
      at: new Date().toISOString(),
    };
    setVersionHistory(prev => {
      const key = `${type}:${record.id}`;
      const existing = prev[key] || [];
      return { ...prev, [key]: [entry, ...existing].slice(0, 20) };
    });
    // Persist to server
    const tableMap = { active: 'active_grants', app: 'pipeline_apps' };
    if (tableMap[type]) {
      apiFetch(`${API}/versions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ table: tableMap[type], recordId: record.id, snapshot: record, savedBy: authUser?.name || authUser?.email || '' }),
      }).catch(() => {});
    }
  };

  const openMoveMenu = (e, id) => {
    e.stopPropagation();
    if (moveMenuId === id) { setMoveMenuId(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    setMoveMenuId(id);
  };

  useEffect(() => {
    if (!moveMenuId) return;
    const close = () => setMoveMenuId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [moveMenuId]);

  const persistActive     = (data) => { const d = data.map((g,i)=>({...g,sn:i+1})); persistRecordsDiff('active-grants',     activeGrants,     d, canEdit); setActiveGrantsState(d);     };
  const persistApps       = (data) => { const d = data.map((a,i)=>({...a,sn:i+1})); persistRecordsDiff('pipeline-apps',     applications,     d, canEdit); setApplicationsState(d);     };
  const persistTerminated = (data) => { const d = data.map((g,i)=>({...g,sn:i+1})); persistRecordsDiff('terminated-grants', terminatedGrants, d, canEdit); setTerminatedGrantsState(d); };
  const persistCompleted  = (data) => { const d = data.map((g,i)=>({...g,sn:i+1})); persistRecordsDiff('completed-grants',  completedGrants,  d, canEdit); setCompletedGrantsState(d);  };

  const handleImport = (table, records) => {
    if      (table === 'active-grants')     persistActive([...activeGrants, ...records]);
    else if (table === 'pipeline-apps')     persistApps([...applications, ...records]);
    else if (table === 'completed-grants')  persistCompleted([...completedGrants, ...records]);
    else if (table === 'terminated-grants') persistTerminated([...terminatedGrants, ...records]);
    setShowImport(false);
  };

  const funderList = useMemo(() => {
    const map = {};
    const add = (nameRaw, type, val) => {
      if (!nameRaw) return;
      const key = nameRaw.trim().toLowerCase();
      if (!map[key]) map[key] = { name: nameRaw.trim(), active: 0, completed: 0, terminated: 0, totalVal: 0 };
      map[key][type]++;
      map[key].totalVal += parseUSD(val);
    };
    activeGrants.forEach(g => add(g.donor, "active", g.awardedAmount));
    completedGrants.forEach(g => add(g.funder, "completed", g.awardedAmount));
    terminatedGrants.forEach(g => add(g.funder, "terminated", g.awardedAmount));
    return Object.values(map).sort((a, b) => {
      const at = a.active + a.completed + a.terminated;
      const bt = b.active + b.completed + b.terminated;
      return bt !== at ? bt - at : b.totalVal - a.totalVal;
    });
  }, [activeGrants, completedGrants, terminatedGrants]);

  const addNote = (text) => {
    if (!text.trim() || !detailItem) return;
    const note = { id: generateId(), text: text.trim(), ts: new Date().toISOString() };
    const updated = { ...detailItem, _notes: [...(detailItem._notes || []), note] };
    setDetailItem(updated);
    if (detailType === "active") persistActive(activeGrants.map(g => g.id === updated.id ? updated : g));
    else if (detailType === "app") persistApps(applications.map(a => a.id === updated.id ? updated : a));
    else if (detailType === "terminated") persistTerminated(terminatedGrants.map(g => g.id === updated.id ? updated : g));
    else if (detailType === "completed") persistCompleted(completedGrants.map(g => g.id === updated.id ? updated : g));
    setNoteInput("");
  };

  const deleteNote = (noteId) => {
    if (!detailItem) return;
    const updated = { ...detailItem, _notes: (detailItem._notes || []).filter(n => n.id !== noteId) };
    setDetailItem(updated);
    if (detailType === "active") persistActive(activeGrants.map(g => g.id === updated.id ? updated : g));
    else if (detailType === "app") persistApps(applications.map(a => a.id === updated.id ? updated : a));
    else if (detailType === "terminated") persistTerminated(terminatedGrants.map(g => g.id === updated.id ? updated : g));
    else if (detailType === "completed") persistCompleted(completedGrants.map(g => g.id === updated.id ? updated : g));
  };

  const handleMoveTo = (grant, from, to) => {
    // Remove from source list
    if      (from === "active")     persistActive(activeGrants.filter(g => g.id !== grant.id));
    else if (from === "terminated") persistTerminated(terminatedGrants.filter(g => g.id !== grant.id));
    else if (from === "completed")  persistCompleted(completedGrants.filter(g => g.id !== grant.id));

    // Build destination record and add to target list
    if (to === "active") {
      const restored = {
        id: grant.id, sn: 0,
        donor:       grant.funder       || grant.donor       || "",
        grantNumber: grant.awardNumber  || grant.grantNumber || "",
        pi:          grant.pi           || "",
        shortTitle:  "",
        title:       grant.title        || "",
        type:        "",
        states:      "",
        totalUSD:    grant.awardedAmount || grant.totalUSD   || "",
        fy26:        "",
        primeOrSub:  grant.primeOrSub   || "",
        start:       "",
        end:         "",
        poc:         [grant.poc1, grant.poc2].filter(Boolean).join(", ") || grant.poc || "",
        brief:       "",
      };
      persistActive([...activeGrants, restored]);
    } else {
      const pocs = (grant.poc || "").split(/[,&]/).map(p => p.trim());
      const moved = {
        id:           grant.id, sn: 0,
        funder:       grant.donor       || grant.funder       || "",
        awardNumber:  grant.grantNumber || grant.awardNumber  || "",
        primeOrSub:   grant.primeOrSub  || "",
        pi:           grant.pi          || "",
        title:        grant.title       || "",
        awardedAmount:grant.totalUSD    || grant.awardedAmount|| "",
        duration:     grant.duration    || "",
        year:         grant.year        || `${grant.start || ""}–${grant.end || ""}`,
        poc1:         grant.poc1        || pocs[0]            || "",
        poc2:         grant.poc2        || pocs[1]            || "",
        ...(to === "terminated" ? { remarks: grant.remarks || "" } : {}),
      };
      if (to === "terminated") persistTerminated([...terminatedGrants, moved]);
      else persistCompleted([...completedGrants, moved]);
    }

    setMoveMenuId(null);
    if (detailItem?.id === grant.id) closeDetail();
  };
  const openDetail  = (item, type) => { setDetailItem(item); setDetailType(type); };
  const closeDetail = () => { setDetailItem(null); setDetailType(null); };
  const openEdit    = (item, type) => { setEditItem(item); setEditType(type); setFormData(item ? { ...item } : {}); };
  const closeEdit   = () => { setEditItem(null); setEditType(null); setFormData({}); };
  const handleSaveActive = () => {
    const item = { ...formData, brief: formData.brief || "" };
    if (editItem?.id) {
      const prev = activeGrants.find(g => g.id === item.id);
      if (prev) saveVersion(prev, "active");
      persistActive(activeGrants.map(g => g.id === item.id ? item : g));
    } else {
      persistActive([...activeGrants, { ...item, id: `ag-${generateId()}` }]);
    }
    closeEdit();
  };
  const handleSaveApp = () => {
    const item = { ...formData, brief: formData.brief || "" };
    if (editItem?.id) {
      const prev = applications.find(a => a.id === item.id);
      if (prev) saveVersion(prev, "app");
      persistApps(applications.map(a => a.id === item.id ? item : a));
    } else {
      persistApps([...applications, { ...item, id: `ap-${generateId()}` }]);
    }
    closeEdit();
  };
  const handleDelete = () => {
    if (!deleteTarget) return;
    let original = null;
    if (deleteTarget.type === "active") {
      original = activeGrants.find(g => g.id === deleteTarget.id);
      persistActive(activeGrants.filter(g => g.id !== deleteTarget.id));
    } else {
      original = applications.find(a => a.id === deleteTarget.id);
      persistApps(applications.filter(a => a.id !== deleteTarget.id));
    }
    if (original) {
      setRecycleBin(prev => [...prev, { ...original, _recycled_type: deleteTarget.type, _deleted_at: new Date().toISOString() }]);
      // Persist to server
      const tableMap = { active: 'active_grants', app: 'pipeline_apps' };
      apiFetch(`${API}/recycle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ table: tableMap[deleteTarget.type], originalId: original.id, data: original, workspaceId: localStorage.getItem('gos_ws_owner') || null }),
      }).catch(() => {});
    }
    if (detailItem?.id === deleteTarget.id) closeDetail();
    setDeleteTarget(null);
  };

  const handleRestore = (item) => {
    if (item._binId) {
      // Server-backed item
      apiFetch(`${API}/recycle/${item._binId}/restore`, { method: 'POST' })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (!d?.ok) return;
          setBinItems(prev => prev.filter(it => it.id !== item._binId));
          const reloadTable = d.table === 'active_grants' ? 'active-grants' : 'pipeline-apps';
          apiFetch(`${API}/db/${reloadTable}`).then(r => r.json()).then(d2 => {
            if (d2.records) {
              if (reloadTable === 'active-grants') setActiveGrantsState(d2.records.map((g, i) => ({ ...g, sn: i + 1 })));
              else setApplicationsState(d2.records.map((a, i) => ({ ...a, sn: i + 1 })));
            }
          }).catch(() => {});
        }).catch(() => {});
    } else {
      // localStorage item (legacy)
      const clean = { ...item };
      delete clean._recycled_type;
      delete clean._deleted_at;
      if (item._recycled_type === "active") persistActive([...activeGrants, clean]);
      else persistApps([...applications, clean]);
      setRecycleBin(prev => prev.filter(it => it.id !== item.id || it._deleted_at !== item._deleted_at));
    }
  };

  const handlePermDelete = (item) => {
    if (item._binId) {
      apiFetch(`${API}/recycle/${item._binId}`, { method: 'DELETE' }).catch(() => {});
      setBinItems(prev => prev.filter(it => it.id !== item._binId));
    } else {
      setRecycleBin(prev => prev.filter(it => it.id !== item.id || it._deleted_at !== item._deleted_at));
    }
  };

  // Portfolio computed
  const totalFY26    = activeGrants.reduce((s, g) => s + parseUSD(g.fy26), 0);
  const primeCount   = activeGrants.filter((g) => g.primeOrSub === "Prime").length;
  const subCount     = activeGrants.filter((g) => g.primeOrSub === "Sub").length;
  const endingSoon   = activeGrants.filter((g) => { const d = daysUntilDeadline(g.end); return d !== null && d >= 0 && d <= 90; }).length;

  const pocOptions = [...new Set(activeGrants.flatMap(g => [g.poc1, g.poc2].filter(Boolean)))].sort();

  const filteredGrants = activeGrants.filter((g) => {
    if (portFilter === "prime"  && g.primeOrSub !== "Prime") return false;
    if (portFilter === "sub"    && g.primeOrSub !== "Sub")   return false;
    if (portFilter === "ending") { const d = daysUntilDeadline(g.end); if (d === null || d < 0 || d > 90) return false; }
    if (portPOC !== "all" && g.poc1 !== portPOC && g.poc2 !== portPOC) return false;
    if (debouncedPortSearch) { const s = debouncedPortSearch.toLowerCase(); return [g.donor, g.title, g.shortTitle, g.states, g.pi, g.poc1, g.poc2].some((f) => (f || "").toLowerCase().includes(s)); }
    return true;
  });
  const sortedGrants = [...filteredGrants].sort((a, b) => {
    if (portSort === "amount_desc") return parseAmt(b.totalUSD) - parseAmt(a.totalUSD);
    if (portSort === "amount_asc")  return parseAmt(a.totalUSD) - parseAmt(b.totalUSD);
    if (portSort === "fy26_desc")   return parseAmt(b.fy26)     - parseAmt(a.fy26);
    return new Date(a.end) - new Date(b.end); // default: end date
  });

  // Applications computed
  const appStats = {
    awarded:      applications.filter((a) => a.awardStatus === "Awarded").length,
    pending:      applications.filter((a) => a.awardStatus === "Pending").length,
    notAwarded:   applications.filter((a) => a.awardStatus === "Not Awarded").length,
    notSubmitted: applications.filter((a) => a.status === "Not Submitted").length,
  };
  const successRate = appStats.awarded + appStats.notAwarded > 0
    ? Math.round((appStats.awarded / (appStats.awarded + appStats.notAwarded)) * 100) : null;
  const filteredApps = applications.filter((a) => {
    if (appFilter === "awarded"       && a.awardStatus !== "Awarded")      return false;
    if (appFilter === "pending"       && a.awardStatus !== "Pending")      return false;
    if (appFilter === "not_awarded"   && a.awardStatus !== "Not Awarded")  return false;
    if (appFilter === "not_submitted" && a.status !== "Not Submitted")     return false;
    if (debouncedAppSearch) { const s = debouncedAppSearch.toLowerCase(); return [a.funder, a.title, a.pi, a.rfaNumber].some((f) => (f || "").toLowerCase().includes(s)); }
    return true;
  });
  const sortedApps = [...filteredApps].sort((a, b) => new Date(b.deadline) - new Date(a.deadline));

  // Tracked sorted
  const sortedTracked = [...tracked].sort((a, b) => {
    const da = daysUntilDeadline(a.deadline), db = daysUntilDeadline(b.deadline);
    if (da === null && db === null) return 0;
    if (da === null) return 1; if (db === null) return -1;
    return da - db;
  });
  const STATUS_OPTS = ["tracking", "preparing", "submitted", "awarded", "declined"];


  const StatCard = ({ label, value, color, accent }) => (
    <div style={{
      flex: 1, minWidth: 90, background: T.bgSurface, border: `1px solid ${T.border}`,
      borderTop: `3px solid ${accent || T.gold}`, borderRadius: 8, padding: "16px 18px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.25)",
    }}>
      <div style={{ fontSize: 26, fontWeight: 400, color: color || T.text, fontFamily: GARAMOND, lineHeight: 1.1, letterSpacing: "-0.02em" }}>{value}</div>
      <div style={{ fontSize: 10, color: T.textFaint, marginTop: 6, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: GARAMOND }}>{label}</div>
    </div>
  );

  const filterChips = (opts, active, setter) =>
    opts.map(([val, label]) => (
      <button key={val} onClick={() => setter(val)} style={{
        padding: "5px 14px", fontSize: 11, borderRadius: 20, cursor: "pointer",
        background: active === val ? T.gold : T.bgElevated,
        color:      active === val ? "#0A1628" : T.textFaint,
        border:     `1px solid ${active === val ? T.gold : T.border}`,
        fontFamily: GARAMOND, fontWeight: active === val ? 600 : 400,
        transition: "all 0.15s", letterSpacing: "0.02em",
      }}>
        {label}
      </button>
    ));


  const ExportBtn = ({ onClick, label = "⬇ Download Excel" }) => (
    <button onClick={onClick} style={{
      padding: "7px 18px", fontSize: 11, borderRadius: 6, cursor: "pointer",
      background: T.gold, color: "#0A1628", border: "none",
      fontFamily: GARAMOND, fontWeight: 600, letterSpacing: "0.03em",
      boxShadow: "0 1px 4px rgba(201,162,39,0.3)", transition: "opacity 0.15s",
    }}>
      {label}
    </button>
  );

  const DetailRow = ({ label, value }) => value ? (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: T.gold, fontFamily: MONO, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: T.textBody, fontFamily: GARAMOND, lineHeight: 1.6 }}>{value}</div>
    </div>
  ) : null;



  const _sf = selectedFunder ? selectedFunder.trim().toLowerCase() : null;
  const fActiveGrants     = _sf ? activeGrants.filter(g    => (g.donor  || "").trim().toLowerCase() === _sf) : [];
  const fCompletedGrants  = _sf ? completedGrants.filter(g  => (g.funder || "").trim().toLowerCase() === _sf) : [];
  const fTerminatedGrants = _sf ? terminatedGrants.filter(g => (g.funder || "").trim().toLowerCase() === _sf) : [];
  const funderInfo = _sf ? funderList.find(f => f.name.trim().toLowerCase() === _sf) : null;
  const funderAllGrants = [
    ...fActiveGrants.map(g    => ({ ...g, _type: "active" })),
    ...fCompletedGrants.map(g  => ({ ...g, _type: "completed" })),
    ...fTerminatedGrants.map(g => ({ ...g, _type: "terminated" })),
  ];

  return (
    <>
    <div style={{ flex: 1, overflowY: "auto", height: "100%", padding: isMobile ? "14px 12px" : "24px 32px" }}>
      {dbLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: T.textMuted, fontSize: 13, fontFamily: FONT_UI, marginBottom: 16 }}>
          <div style={{ width: 14, height: 14, border: `2px solid ${T.border}`, borderTopColor: TOKENS.gold, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          Syncing with database…
        </div>
      )}

      {!canEdit && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", marginBottom: 16, background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.25)", borderRadius: 8, fontSize: 12, color: "#93c5fd", fontFamily: FONT_UI }}>
          <span>👁</span>
          <span><strong style={{ color: "#60a5fa" }}>View-only access.</strong> You can browse this workspace but cannot save changes. To gain edit access, the workspace owner must add your email as a POC on a grant.</span>
        </div>
      )}

      {/* ── ACTIVE GRANTS ── */}
      {subTab === "active" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <div>
              <h2 style={{ margin: 0, fontFamily: GARAMOND, fontSize: 28, fontWeight: 400, color: T.text, letterSpacing: "-0.02em" }}>Active Portfolio</h2>
              <div style={{ fontSize: 14, color: T.slateBlue, fontFamily: GARAMOND, fontStyle: "italic", marginTop: 4 }}>
                {activeGrants.length} grants currently under management
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => openEdit(null, "active")} style={{ padding: "7px 16px", fontSize: 11, borderRadius: 6, cursor: "pointer", background: T.slateBlue, color: "#fff", border: "none", fontFamily: GARAMOND, fontWeight: 600, letterSpacing: "0.03em" }}>+ Add Grant</button>
              <button onClick={() => { setImportDefaultTable('active-grants'); setShowImport(true); }} style={{ padding: "7px 14px", fontSize: 11, borderRadius: 6, cursor: "pointer", background: "transparent", color: T.gold, border: `1px solid ${T.gold}`, fontFamily: GARAMOND, fontWeight: 600 }}>⬆ Import</button>
              <ExportBtn onClick={() => exportGOSExcel(sortedGrants, ACTIVE_COLS, "Active Grants", "ActiveGrants")} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
            <StatCard label="Active Grants" value={activeGrants.length} color={T.text}      accent={T.gold} />
            <StatCard label="FY26 Funding"  value={formatUSD(totalFY26)} color="#059669"     accent="#059669" />
            <StatCard label="Prime Awards"  value={primeCount}           color={T.slateBlue} accent={T.slateBlue} />
            <StatCard label="Subawards"     value={subCount}             color={T.textMuted} accent={T.border} />
            <StatCard label="Ending ≤ 90d"  value={endingSoon}           color={endingSoon > 0 ? "#D97706" : T.textFaint} accent={endingSoon > 0 ? "#D97706" : T.border} />
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
            {filterChips([["all","All Grants"],["prime","Prime Only"],["sub","Subawards"],["ending","Ending Soon"]], portFilter, setPortFilter)}
            <select value={portSort} onChange={e => setPortSort(e.target.value)} style={{ marginLeft: 4, padding: "5px 10px", fontSize: 11, borderRadius: 20, background: T.bgElevated, border: `1px solid ${T.border}`, color: T.textFaint, fontFamily: GARAMOND, cursor: "pointer", outline: "none" }}>
              <option value="end">Sort: End Date</option>
              <option value="amount_desc">Sort: Total ↑ Highest</option>
              <option value="amount_asc">Sort: Total ↓ Lowest</option>
              <option value="fy26_desc">Sort: FY26 ↑ Highest</option>
            </select>
            {pocOptions.length > 0 && (
              <select value={portPOC} onChange={e => setPortPOC(e.target.value)} style={{ padding: "5px 10px", fontSize: 11, borderRadius: 20, background: T.bgElevated, border: `1px solid ${T.border}`, color: portPOC !== "all" ? T.gold : T.textFaint, fontFamily: GARAMOND, cursor: "pointer", outline: "none" }}>
                <option value="all">All POCs</option>
                {pocOptions.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
            <SearchInput val={portSearch} setter={setPortSearch} ph="Search donor, title, state, PI…" />
          </div>
          <GrantList
            items={sortedGrants}
            viewType="active"
            T={T}
            onCardClick={(g) => openDetail(g, "active")}
            renderRowActions={(g) => (
              <div style={{ display: "flex", gap: 5 }}>
                <DriveFolderButton grantId={g.id} grantName={g.shortTitle || g.title || g.id} T={T} />
                <GatedButton feature="versionHistory" T={T}>
                  <button onClick={e => { e.stopPropagation(); setHistoryTarget({ id: g.id, type: "active", name: g.shortTitle || g.title }); }} title="Version history" style={{ padding: "3px 8px", fontSize: 10, borderRadius: 4, cursor: "pointer", background: "transparent", color: T.slateBlue, border: `1px solid ${T.slateBlue}`, fontFamily: GARAMOND, display: "flex", alignItems: "center", gap: 3 }}><Clock size={10} />History</button>
                </GatedButton>
                <button onClick={e => openMoveMenu(e, g.id)} style={{ padding: "3px 12px", fontSize: 11, borderRadius: 4, cursor: "pointer", background: T.bgElevated, color: T.textMuted, border: `1px solid ${T.border}`, fontFamily: GARAMOND, letterSpacing: "0.04em" }}>⋯</button>
              </div>
            )}
            emptyNode={
              activeGrants.length === 0 ? (
                <div style={{ textAlign: "center", paddingTop: 60, paddingBottom: 40 }}>
                  <div style={{ marginBottom: 14, display: "flex", justifyContent: "center" }}>
                    <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                      <rect x="4" y="16" width="52" height="38" rx="6" fill="rgba(91,79,233,0.07)" stroke="rgba(91,79,233,0.22)" strokeWidth="1.5"/>
                      <path d="M4 26h52" stroke="rgba(91,79,233,0.18)" strokeWidth="1"/>
                      <rect x="4" y="10" width="24" height="12" rx="4" fill="rgba(91,79,233,0.12)" stroke="rgba(91,79,233,0.22)" strokeWidth="1.5"/>
                      <rect x="20" y="34" width="20" height="2.5" rx="1.25" fill="rgba(91,79,233,0.3)"/>
                      <rect x="20" y="41" width="16" height="2.5" rx="1.25" fill="rgba(91,79,233,0.2)"/>
                      <rect x="20" y="48" width="12" height="2.5" rx="1.25" fill="rgba(91,79,233,0.12)"/>
                    </svg>
                  </div>
                  <div style={{ fontSize: 18, fontFamily: GARAMOND, color: T.text, marginBottom: 8 }}>No active grants yet</div>
                  <div style={{ fontSize: 13, color: T.textMuted, fontFamily: GARAMOND, maxWidth: 380, margin: "0 auto 24px", lineHeight: 1.6 }}>
                    This is your grant portfolio. Add grants you're currently managing — track funding amounts, end dates, and Point of Contact.
                  </div>
                  <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                    <button onClick={() => openEdit(null, "active")} style={{ padding: "9px 20px", background: TOKENS.slateBlue, color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: FONT_UI, cursor: "pointer" }}>+ Add Your First Grant</button>
                    <button onClick={() => { setImportDefaultTable("active-grants"); setShowImport(true); }} style={{ padding: "9px 20px", background: "transparent", color: T.textMuted, border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 12, fontFamily: FONT_UI, cursor: "pointer" }}>Import Grants</button>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: "center", color: T.textFaint, paddingTop: 48, fontSize: 14, fontFamily: GARAMOND }}>
                  No grants match your filter.
                </div>
              )
            }
          />
        </div>
      )}

      {/* ── TERMINATED ── */}
      {subTab === "terminated" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <div>
              <h2 style={{ margin: 0, fontFamily: GARAMOND, fontSize: 28, fontWeight: 400, color: T.text, letterSpacing: "-0.02em" }}>Terminated Grants</h2>
              <div style={{ fontSize: 14, color: T.slateBlue, fontFamily: GARAMOND, fontStyle: "italic", marginTop: 4 }}>
                {terminatedGrants.length} grant{terminatedGrants.length !== 1 ? "s" : ""} terminated before completion
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { setImportDefaultTable('terminated-grants'); setShowImport(true); }} style={{ padding: "7px 14px", fontSize: 11, borderRadius: 6, cursor: "pointer", background: "transparent", color: T.gold, border: `1px solid ${T.gold}`, fontFamily: GARAMOND, fontWeight: 600 }}>⬆ Import</button>
              {terminatedGrants.length > 0 && <ExportBtn onClick={() => exportGOSExcel(terminatedGrants, TERMINATED_COLS, "Terminated Grants", "TerminatedGrants")} />}
            </div>}
          </div>
          <GrantList
            items={terminatedGrants}
            viewType="terminated"
            T={T}
            onCardClick={(g) => openDetail(g, "terminated")}
            renderRowActions={(g) => (
              <div style={{ display: "flex", gap: 5 }}>
                <DriveFolderButton grantId={g.id} grantName={g.shortTitle || g.title || g.id} T={T} />
                <button
                  onClick={e => openMoveMenu(e, g.id)}
                  style={{ padding: "3px 10px", fontSize: 10, borderRadius: 4, cursor: "pointer", background: T.bgElevated, color: T.textMuted, border: `1px solid ${T.border}`, fontFamily: GARAMOND }}
                >Move ▾</button>
              </div>
            )}
            emptyNode={
              <div style={{ textAlign: "center", color: T.textFaint, paddingTop: 80, fontFamily: GARAMOND }}>
                <div style={{ fontSize: 13, marginBottom: 8 }}>No terminated grants on record.</div>
              </div>
            }
          />
        </div>
      )}

      {/* ── APPLICATIONS ── */}
      {subTab === "applications" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <div>
              <h2 style={{ margin: 0, fontFamily: GARAMOND, fontSize: 28, fontWeight: 400, color: T.text, letterSpacing: "-0.02em" }}>Applications Pipeline</h2>
              <div style={{ fontSize: 14, color: T.slateBlue, fontFamily: GARAMOND, fontStyle: "italic", marginTop: 4 }}>
                {applications.length} applications tracked
                {successRate !== null && <span style={{ marginLeft: 12, color: "#059669" }}>· {successRate}% success rate</span>}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => openEdit(null, "app")} style={{ padding: "7px 16px", fontSize: 11, borderRadius: 6, cursor: "pointer", background: T.slateBlue, color: "#fff", border: "none", fontFamily: GARAMOND, fontWeight: 600, letterSpacing: "0.03em" }}>+ Add Application</button>
              <button onClick={() => { setImportDefaultTable('pipeline-apps'); setShowImport(true); }} style={{ padding: "7px 14px", fontSize: 11, borderRadius: 6, cursor: "pointer", background: "transparent", color: T.gold, border: `1px solid ${T.gold}`, fontFamily: GARAMOND, fontWeight: 600 }}>⬆ Import</button>
              <ExportBtn onClick={() => exportGOSExcel(sortedApps, APP_COLS, "New Applications", "NewApplications")} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
            <StatCard label="Total"         value={applications.length}   color={T.text}      accent={T.gold} />
            <StatCard label="Awarded"       value={appStats.awarded}      color="#059669"     accent="#059669" />
            <StatCard label="Pending"       value={appStats.pending}      color="#D97706"     accent="#D97706" />
            <StatCard label="Not Awarded"   value={appStats.notAwarded}   color="#DC2626"     accent="#DC2626" />
            <StatCard label="Not Submitted" value={appStats.notSubmitted} color={T.textMuted} accent={T.border} />
            {successRate !== null && <StatCard label="Success Rate" value={`${successRate}%`} color={T.gold} accent={T.gold} />}
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
            {filterChips([["all","All"],["awarded","Awarded"],["pending","Pending"],["not_awarded","Not Awarded"],["not_submitted","Not Submitted"]], appFilter, setAppFilter)}
            <SearchInput val={appSearch} setter={setAppSearch} ph="Search funder, title, PI…" />
          </div>
          <GrantList
            items={sortedApps}
            viewType="apps"
            T={T}
            onCardClick={(a) => openDetail(a, "app")}
            renderRowActions={(a) => (
              <div style={{ display: "flex", gap: 5 }}>
                <DriveFolderButton grantId={a.id} grantName={a.title || a.id} T={T} />
                <button onClick={e => { e.stopPropagation(); openEdit(a, "app"); }} style={{ padding: "3px 10px", fontSize: 10, borderRadius: 4, cursor: "pointer", background: T.bgElevated, color: T.textFaint, border: `1px solid ${T.border}`, fontFamily: GARAMOND }}>Edit</button>
                <GatedButton feature="versionHistory" T={T}>
                  <button onClick={e => { e.stopPropagation(); setHistoryTarget({ id: a.id, type: "app", name: a.title }); }} title="Version history" style={{ padding: "3px 8px", fontSize: 10, borderRadius: 4, cursor: "pointer", background: "transparent", color: T.slateBlue, border: `1px solid ${T.slateBlue}`, fontFamily: GARAMOND, display: "flex", alignItems: "center", gap: 3 }}><Clock size={10} />History</button>
                </GatedButton>
                <button onClick={e => { e.stopPropagation(); setDeleteTarget({ id: a.id, type: "app", name: a.title }); }} style={{ padding: "3px 10px", fontSize: 10, borderRadius: 4, cursor: "pointer", background: "transparent", color: "#DC2626", border: "1px solid #DC2626", fontFamily: GARAMOND }}>Delete</button>
              </div>
            )}
            emptyNode={
              applications.length === 0 ? (
                <div style={{ textAlign: "center", paddingTop: 60, paddingBottom: 40 }}>
                  <div style={{ marginBottom: 14, display: "flex", justifyContent: "center" }}>
                    <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                      <rect x="10" y="8" width="40" height="48" rx="5" fill="rgba(91,79,233,0.07)" stroke="rgba(91,79,233,0.22)" strokeWidth="1.5"/>
                      <rect x="22" y="4" width="16" height="10" rx="3" fill="rgba(91,79,233,0.18)" stroke="rgba(91,79,233,0.3)" strokeWidth="1.5"/>
                      <rect x="18" y="24" width="4" height="4" rx="1" fill="rgba(91,79,233,0.35)"/>
                      <rect x="26" y="24" width="16" height="2.5" rx="1.25" fill="rgba(91,79,233,0.25)"/>
                      <rect x="18" y="32" width="4" height="4" rx="1" fill="rgba(91,79,233,0.2)"/>
                      <rect x="26" y="32" width="12" height="2.5" rx="1.25" fill="rgba(91,79,233,0.18)"/>
                      <rect x="18" y="40" width="4" height="4" rx="1" fill="rgba(91,79,233,0.12)"/>
                      <rect x="26" y="40" width="8" height="2.5" rx="1.25" fill="rgba(91,79,233,0.12)"/>
                    </svg>
                  </div>
                  <div style={{ fontSize: 18, fontFamily: GARAMOND, color: T.text, marginBottom: 8 }}>No applications yet</div>
                  <div style={{ fontSize: 13, color: T.textMuted, fontFamily: GARAMOND, maxWidth: 380, margin: "0 auto 24px", lineHeight: 1.6 }}>
                    Track every grant application you submit — log the funder, deadline, award status, and budget to measure your success rate over time.
                  </div>
                  <button onClick={() => openEdit(null, "app")} style={{ padding: "9px 20px", background: TOKENS.slateBlue, color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: FONT_UI, cursor: "pointer" }}>+ Log Your First Application</button>
                </div>
              ) : (
                <div style={{ textAlign: "center", color: T.textFaint, paddingTop: 48, fontSize: 14, fontFamily: GARAMOND }}>
                  No applications match your filter.
                </div>
              )
            }
          />
        </div>
      )}

      {/* ── TRACKED ── */}
      {subTab === "tracked" && (
        <div>
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ margin: 0, fontFamily: GARAMOND, fontSize: 28, fontWeight: 400, color: T.text, letterSpacing: "-0.02em" }}>Tracked Grants</h2>
            <div style={{ fontSize: 14, color: T.slateBlue, fontFamily: GARAMOND, fontStyle: "italic", marginTop: 4 }}>
              {tracked.length} grant{tracked.length !== 1 ? "s" : ""} tracked from extraction tool
            </div>
          </div>
          {tracked.length === 0 ? (
            <div style={{ textAlign: "center", paddingTop: 80 }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                  <rect x="8" y="12" width="44" height="40" rx="6" fill="rgba(91,79,233,0.07)" stroke="rgba(91,79,233,0.22)" strokeWidth="1.5"/>
                  <path d="M8 24h44" stroke="rgba(91,79,233,0.18)" strokeWidth="1"/>
                  <circle cx="20" cy="18" r="2.5" fill="rgba(91,79,233,0.35)"/>
                  <circle cx="30" cy="18" r="2.5" fill="rgba(91,79,233,0.35)"/>
                  <circle cx="40" cy="18" r="2.5" fill="rgba(91,79,233,0.35)"/>
                  <rect x="16" y="32" width="10" height="10" rx="2.5" fill="rgba(91,79,233,0.2)"/>
                  <rect x="30" y="32" width="14" height="2.5" rx="1.25" fill="rgba(91,79,233,0.22)"/>
                  <rect x="30" y="37" width="10" height="2.5" rx="1.25" fill="rgba(91,79,233,0.14)"/>
                  <circle cx="46" cy="44" r="8" fill="rgba(201,162,39,0.15)" stroke="rgba(201,162,39,0.4)" strokeWidth="1.5"/>
                  <path d="M46 41v4l2.5 2" stroke="rgba(201,162,39,0.7)" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <div style={{ fontSize: 15, color: T.textFaint, fontFamily: GARAMOND }}>No grants tracked yet.</div>
              <div style={{ fontSize: 13, marginTop: 8, fontFamily: GARAMOND, color: T.textShadow }}>Extract a grant and click "📅 Track Deadline" to add it here.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {sortedTracked.map((grant) => {
                const days = daysUntilDeadline(grant.deadline);
                const u = days === null ? "neutral" : days < 0 ? "past" : days <= 7 ? "critical" : days <= 30 ? "warning" : "ok";
                const c = {
                  critical: { border: "#DC2626", left: "#DC2626", text: "#f87171" },
                  warning:  { border: "#D97706", left: "#D97706", text: "#fbbf24" },
                  ok:       { border: T.border,  left: "#059669", text: "#059669" },
                  past:     { border: T.border,  left: T.textGhost, text: T.textGhost },
                  neutral:  { border: T.border,  left: T.textShadow, text: T.textFaint },
                }[u];
                return (
                  <div key={grant.id} style={{
                    background: T.bgSurface, border: `1px solid ${T.border}`, borderLeft: `4px solid ${c.left}`,
                    borderRadius: 8, padding: "14px 18px",
                    boxShadow: "none",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, color: T.text, fontFamily: GARAMOND, marginBottom: 3 }}>{grant.title}</div>
                        <div style={{ fontSize: 12, color: T.textFaint, fontFamily: GARAMOND }}>{grant.funder}{grant.awardMax ? ` · Up to ${grant.awardMax}` : ""}</div>
                        <div style={{ fontSize: 11, color: T.textShadow, marginTop: 2, fontFamily: GARAMOND }}>{grant.type}</div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 600, color: c.text, fontFamily: MONO }}>
                          {days === null ? "—" : days < 0 ? `${Math.abs(days)}d ago` : days === 0 ? "TODAY" : `${days}d left`}
                        </div>
                        <div style={{ fontSize: 11, color: T.textGhost, marginTop: 2, fontFamily: MONO }}>{grant.deadline}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
                      {STATUS_OPTS.map((s) => (
                        <button key={s} onClick={() => persistTracked(tracked.map(g => g.id === grant.id ? { ...g, status: s } : g))} style={{
                          padding: "3px 10px", fontSize: 10, borderRadius: 4, cursor: "pointer",
                          background: grant.status === s ? T.gold : T.bgElevated,
                          color:      grant.status === s ? "#0A1628" : T.textFaint,
                          border:     `1px solid ${grant.status === s ? T.gold : T.border}`,
                          fontFamily: GARAMOND, transition: "all 0.15s",
                        }}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                      ))}
                      <button onClick={() => persistTracked(tracked.filter(g => g.id !== grant.id))} style={{
                        marginLeft: "auto", padding: "3px 10px", fontSize: 10, borderRadius: 4, cursor: "pointer",
                        background: "transparent", color: "#DC2626", border: "1px solid #DC2626", fontFamily: GARAMOND,
                      }}>
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── FUNDERS ── */}
      {subTab === "funders" && (
        selectedFunder ? (
          /* ── Funder detail view ── */
          <div>
            <button onClick={() => setSelectedFunder(null)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontFamily: GARAMOND, fontSize: 13, padding: "0 0 18px 0", display: "flex", alignItems: "center", gap: 6 }}>
              ← Back to Funders
            </button>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
              <div>
                <h2 style={{ margin: 0, fontFamily: GARAMOND, fontSize: 28, fontWeight: 400, color: T.text, letterSpacing: "-0.02em" }}>{selectedFunder}</h2>
                <div style={{ fontSize: 14, color: TOKENS.gold, fontFamily: GARAMOND, fontStyle: "italic", marginTop: 4 }}>
                  {funderInfo ? formatUSD(funderInfo.totalVal) : "—"} total value · {funderAllGrants.length} grant{funderAllGrants.length !== 1 ? "s" : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                {fActiveGrants.length > 0     && <span style={{ ...BADGE_BASE, ...BADGE.green, fontSize: 12 }}>{fActiveGrants.length} Active</span>}
                {fCompletedGrants.length > 0  && <span style={{ ...BADGE_BASE, ...BADGE.gold,  fontSize: 12 }}>{fCompletedGrants.length} Completed</span>}
                {fTerminatedGrants.length > 0 && <span style={{ ...BADGE_BASE, ...BADGE.red,   fontSize: 12 }}>{fTerminatedGrants.length} Terminated</span>}
              </div>
            </div>

            <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
              {/* Header row */}
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr auto" : "1fr 130px 160px 100px", padding: "10px 16px", background: T.bgElevated, borderBottom: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, fontFamily: FONT_UI, textTransform: "uppercase", letterSpacing: "0.08em" }}>Grant</div>
                {!isMobile && <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, fontFamily: FONT_UI, textTransform: "uppercase", letterSpacing: "0.08em", textAlign: "right" }}>Value</div>}
                {!isMobile && <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, fontFamily: FONT_UI, textTransform: "uppercase", letterSpacing: "0.08em", textAlign: "center" }}>Period</div>}
                <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, fontFamily: FONT_UI, textTransform: "uppercase", letterSpacing: "0.08em", textAlign: "right" }}>Status</div>
              </div>

              {funderAllGrants.length === 0 ? (
                <div style={{ padding: "40px 16px", textAlign: "center", color: T.textFaint, fontFamily: GARAMOND }}>No grants found for this funder.</div>
              ) : funderAllGrants.map((g, i) => {
                const badge  = g._type === "active" ? BADGE.green : g._type === "completed" ? BADGE.gold : BADGE.red;
                const label  = g._type === "active" ? "Active"    : g._type === "completed" ? "Completed" : "Terminated";
                const amt    = g.totalUSD || g.awardedAmount || "—";
                const sy     = g.start ? new Date(g.start).getFullYear() : null;
                const ey     = g.end   ? new Date(g.end).getFullYear()   : null;
                const period = sy && ey ? `${sy}–${ey}` : sy ? `${sy}–` : ey ? `–${ey}` : "—";
                const title  = g.shortTitle || g.title || g.grantTitle || "Untitled";
                const rowBg  = i % 2 === 0 ? T.bgSurface : T.bgApp;
                return (
                  <div key={g.id || i}
                    onClick={() => openDetail(g, g._type === "active" ? "active" : g._type === "completed" ? "completed" : "terminated")}
                    style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr auto" : "1fr 130px 160px 100px", padding: "12px 16px", borderBottom: i < funderAllGrants.length - 1 ? `1px solid ${T.border}` : "none", cursor: "pointer", background: rowBg, transition: "background 0.12s" }}
                    onMouseEnter={e => e.currentTarget.style.background = T.bgElevated}
                    onMouseLeave={e => e.currentTarget.style.background = rowBg}
                  >
                    <div>
                      <div style={{ fontSize: 13, color: T.text, fontFamily: GARAMOND, fontWeight: 500, marginBottom: 2 }}>{title}</div>
                      {g.grantNumber && <div style={{ fontSize: 11, color: T.textFaint, fontFamily: MONO }}>{g.grantNumber}</div>}
                      {g.primeOrSub  && <span style={{ ...BADGE_BASE, ...BADGE.blue, fontSize: 10, marginTop: 3, display: "inline-block" }}>{g.primeOrSub}</span>}
                      {isMobile && <div style={{ fontSize: 11, color: T.textFaint, fontFamily: GARAMOND, marginTop: 3 }}>{period}</div>}
                    </div>
                    {!isMobile && <div style={{ fontSize: 14, color: TOKENS.gold, fontFamily: GARAMOND, textAlign: "right", alignSelf: "center" }}>{amt}</div>}
                    {!isMobile && <div style={{ fontSize: 12, color: T.textMuted, fontFamily: GARAMOND, textAlign: "center", alignSelf: "center" }}>{period}</div>}
                    <div style={{ textAlign: "right", alignSelf: "center" }}>
                      <span style={{ ...BADGE_BASE, ...badge, fontSize: 11 }}>{label}</span>
                      {isMobile && <div style={{ fontSize: 13, color: TOKENS.gold, fontFamily: GARAMOND, marginTop: 4 }}>{amt}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* ── Funder grid view ── */
          <div>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontFamily: GARAMOND, fontSize: 28, fontWeight: 400, color: T.text, letterSpacing: "-0.02em" }}>Funders</h2>
              <div style={{ fontSize: 14, color: T.slateBlue, fontFamily: GARAMOND, fontStyle: "italic", marginTop: 4 }}>
                {funderList.length} funder{funderList.length !== 1 ? "s" : ""} across your portfolio
              </div>
            </div>
            {funderList.length === 0 ? (
              <div style={{ padding: "80px 0", textAlign: "center", color: T.textFaint, fontFamily: GARAMOND }}>
                <div style={{ fontSize: 18, marginBottom: 8 }}>No funders yet.</div>
                <div style={{ fontSize: 13 }}>Funders appear automatically as you add grants.</div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill,minmax(300px,1fr))", gap: 14 }}>
                {funderList.map(f => {
                  const total = f.active + f.completed + f.terminated;
                  return (
                    <div key={f.name} onClick={() => setSelectedFunder(f.name)}
                      style={{ background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 8, padding: "16px 18px", cursor: "pointer", transition: "border-color 0.15s", borderLeft: `3px solid ${TOKENS.slateBlue}` }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = TOKENS.slateBlue}
                      onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                        <div style={{ fontSize: 16, fontWeight: 500, color: T.text, fontFamily: GARAMOND, lineHeight: 1.3, flex: 1, minWidth: 0, marginRight: 10 }}>{f.name}</div>
                        <div style={{ fontSize: 13, color: T.textSub, fontFamily: MONO, flexShrink: 0 }}>{total} grant{total !== 1 ? "s" : ""}</div>
                      </div>
                      <div style={{ fontSize: 20, fontFamily: GARAMOND, color: TOKENS.gold, marginBottom: 10 }}>{f.totalVal > 0 ? formatUSD(f.totalVal) : "—"}</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {f.active > 0     && <span style={{ ...BADGE_BASE, ...BADGE.green, fontSize: 11 }}>{f.active} Active</span>}
                        {f.completed > 0  && <span style={{ ...BADGE_BASE, ...BADGE.gold,  fontSize: 11 }}>{f.completed} Completed</span>}
                        {f.terminated > 0 && <span style={{ ...BADGE_BASE, ...BADGE.red,   fontSize: 11 }}>{f.terminated} Terminated</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )
      )}

      {/* ── MY GRANTS ── */}
      {subTab === "myGrants" && (() => {
        const userEmail = (authUser?.email || "").toLowerCase();
        const myActive = activeGrants.filter(g =>
          userEmail && (
            (g.poc1Email || "").toLowerCase() === userEmail ||
            (g.poc2Email || "").toLowerCase() === userEmail
          )
        );
        const myApps = applications.filter(a =>
          userEmail && (
            (a.poc1Email || "").toLowerCase() === userEmail ||
            (a.poc2Email || "").toLowerCase() === userEmail ||
            (a.poc || "").toLowerCase().includes(userEmail.split("@")[0])
          )
        );
        return (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontFamily: GARAMOND, fontSize: 28, fontWeight: 400, color: T.text, letterSpacing: "-0.02em" }}>My Grants</h2>
              <div style={{ fontSize: 14, color: T.slateBlue, fontFamily: GARAMOND, fontStyle: "italic", marginTop: 4 }}>
                Grants and applications where you are listed as POC
              </div>
            </div>
            {!userEmail ? (
              <div style={{ padding: "60px 0", textAlign: "center", color: T.textFaint, fontFamily: GARAMOND }}>
                <div style={{ fontSize: 18, marginBottom: 8 }}>Not signed in</div>
                <div style={{ fontSize: 13 }}>Sign in to see grants assigned to you.</div>
              </div>
            ) : myActive.length === 0 && myApps.length === 0 ? (
              <div style={{ padding: "60px 0", textAlign: "center", color: T.textFaint, fontFamily: GARAMOND }}>
                <div style={{ fontSize: 18, marginBottom: 8 }}>No grants assigned to you yet.</div>
                <div style={{ fontSize: 13 }}>You'll appear here when listed as POC 1 or POC 2 on a grant (with your email entered).</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
                {myActive.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: T.gold, fontFamily: GARAMOND, marginBottom: 12 }}>Active Grants ({myActive.length})</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {myActive.map(g => (
                        <div key={g.id} onClick={() => openDetail(g, "active")} style={{ background: T.bgSurface, border: `1px solid ${T.border}`, borderLeft: `3px solid ${TOKENS.statusGreen}`, borderRadius: 8, padding: "14px 18px", cursor: "pointer", transition: "border-color 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = TOKENS.statusGreen}
                          onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                            <div>
                              <div style={{ fontSize: 15, fontWeight: 500, color: T.text, fontFamily: GARAMOND, marginBottom: 4 }}>{g.shortTitle || g.title || "Untitled"}</div>
                              <div style={{ fontSize: 12, color: T.textFaint, fontFamily: GARAMOND }}>{g.donor} {g.grantNumber ? `· ${g.grantNumber}` : ""}</div>
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                              <div style={{ fontSize: 15, color: TOKENS.gold, fontFamily: GARAMOND }}>{g.totalUSD || g.fy26 || "—"}</div>
                              {g.end && <div style={{ fontSize: 11, color: T.textFaint, fontFamily: GARAMOND, marginTop: 3 }}>Ends {new Date(g.end).toLocaleDateString()}</div>}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                            {g.poc1 && <span style={{ ...BADGE_BASE, ...BADGE.blue, fontSize: 11 }}>POC 1: {g.poc1}</span>}
                            {g.poc2 && <span style={{ ...BADGE_BASE, ...BADGE.blue, fontSize: 11 }}>POC 2: {g.poc2}</span>}
                            {g.primeOrSub && <span style={{ ...BADGE_BASE, ...BADGE.gold, fontSize: 11 }}>{g.primeOrSub}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {myApps.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "#22d3ee", fontFamily: GARAMOND, marginBottom: 12 }}>Applications ({myApps.length})</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {myApps.map(a => (
                        <div key={a.id} onClick={() => openDetail(a, "app")} style={{ background: T.bgSurface, border: `1px solid ${T.border}`, borderLeft: "3px solid #22d3ee", borderRadius: 8, padding: "14px 18px", cursor: "pointer", transition: "border-color 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = "#22d3ee"}
                          onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                            <div>
                              <div style={{ fontSize: 15, fontWeight: 500, color: T.text, fontFamily: GARAMOND, marginBottom: 4 }}>{a.title || "Untitled"}</div>
                              <div style={{ fontSize: 12, color: T.textFaint, fontFamily: GARAMOND }}>{a.funder} {a.rfaNumber ? `· ${a.rfaNumber}` : ""}</div>
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                              <div style={{ fontSize: 13, color: T.textFaint, fontFamily: GARAMOND }}>{a.awardStatus || a.status || "—"}</div>
                              {a.deadline && <div style={{ fontSize: 11, color: T.textFaint, fontFamily: GARAMOND, marginTop: 3 }}>Deadline {new Date(a.deadline).toLocaleDateString()}</div>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── COMPLETED ── */}
      {subTab === "completed" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <div>
              <h2 style={{ margin: 0, fontFamily: GARAMOND, fontSize: 28, fontWeight: 400, color: T.text, letterSpacing: "-0.02em" }}>Completed Grants</h2>
              <div style={{ fontSize: 14, color: T.slateBlue, fontFamily: GARAMOND, fontStyle: "italic", marginTop: 4 }}>
                {completedGrants.length} grant{completedGrants.length !== 1 ? "s" : ""} successfully completed
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { setImportDefaultTable('completed-grants'); setShowImport(true); }} style={{ padding: "7px 14px", fontSize: 11, borderRadius: 6, cursor: "pointer", background: "transparent", color: T.gold, border: `1px solid ${T.gold}`, fontFamily: GARAMOND, fontWeight: 600 }}>⬆ Import</button>
              {completedGrants.length > 0 && <ExportBtn onClick={() => exportGOSExcel(completedGrants, COMPLETED_COLS, "Completed Grants", "CompletedGrants")} />}
            </div>
          </div>
          <GrantList
            items={completedGrants}
            viewType="completed"
            T={T}
            onCardClick={(g) => openDetail(g, "completed")}
            renderRowActions={(g) => (
              <div style={{ display: "flex", gap: 5 }}>
                <DriveFolderButton grantId={g.id} grantName={g.shortTitle || g.title || g.id} T={T} />
                <button
                  onClick={e => openMoveMenu(e, g.id)}
                  style={{ padding: "3px 10px", fontSize: 10, borderRadius: 4, cursor: "pointer", background: T.bgElevated, color: T.textMuted, border: `1px solid ${T.border}`, fontFamily: GARAMOND }}
                >Move ▾</button>
              </div>
            )}
            emptyNode={
              <div style={{ textAlign: "center", color: T.textFaint, paddingTop: 80, fontFamily: GARAMOND }}>
                <div style={{ fontSize: 13 }}>No completed grants on record.</div>
              </div>
            }
          />
        </div>
      )}

      {/* ── RECYCLE BIN ── */}
      {subTab === "recyclebin" && (() => {
        // Normalise server items + legacy localStorage items into a single list
        const serverNorm = binItems.map(it => ({
          ...it.data,
          _binId: it.id,
          _recycled_type: it.table_name === 'active_grants' ? 'active' : 'app',
          _deleted_at: it.deleted_at,
        }));
        const legacyNorm = recycleBin.filter(it => !binItems.some(s => s.original_id === it.id));
        const allItems = [...serverNorm, ...legacyNorm];
        return (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <div>
                <h2 style={{ margin: 0, fontFamily: GARAMOND, fontSize: 28, fontWeight: 400, color: T.text, letterSpacing: "-0.02em" }}>Recycle Bin</h2>
                <div style={{ fontSize: 14, color: T.slateBlue, fontFamily: GARAMOND, fontStyle: "italic", marginTop: 4 }}>
                  {allItems.length} item{allItems.length !== 1 ? "s" : ""} · permanently deleted after 90 days
                </div>
              </div>
            </div>
            {binLoading ? (
              <div style={{ textAlign: "center", color: T.textFaint, paddingTop: 60, fontFamily: GARAMOND }}>Loading…</div>
            ) : allItems.length === 0 ? (
              <div style={{ textAlign: "center", color: T.textFaint, paddingTop: 80, fontFamily: GARAMOND }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🗑️</div>
                <div style={{ fontSize: 14 }}>The recycle bin is empty.</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {allItems.map((item, idx) => {
                  const daysLeft = Math.max(0, 90 - Math.floor((Date.now() - new Date(item._deleted_at).getTime()) / 86400000));
                  const urgent = daysLeft <= 7;
                  return (
                    <div key={item._binId || `${item.id}-${item._deleted_at}`} style={{
                      display: "flex", alignItems: "center", gap: 12,
                      background: T.bgSurface, border: `1px solid ${T.border}`,
                      borderRadius: 8, padding: "12px 16px",
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontFamily: GARAMOND, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {item.shortTitle || item.title || "Untitled"}
                        </div>
                        <div style={{ fontSize: 11, color: T.textMuted, fontFamily: GARAMOND, marginTop: 3 }}>
                          {item._recycled_type === "active" ? "Active Grant" : "Application"} · deleted {new Date(item._deleted_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div style={{ fontSize: 11, fontFamily: GARAMOND, color: urgent ? "#EF4444" : T.textMuted, flexShrink: 0 }}>
                        {daysLeft}d left
                      </div>
                      <button onClick={() => handleRestore(item)} style={{ padding: "5px 12px", fontSize: 11, borderRadius: 5, cursor: "pointer", background: "transparent", color: T.slateBlue, border: `1px solid ${T.slateBlue}`, fontFamily: GARAMOND }}>Restore</button>
                      <button onClick={() => handlePermDelete(item)} style={{ padding: "5px 12px", fontSize: 11, borderRadius: 5, cursor: "pointer", background: "transparent", color: "#DC2626", border: "1px solid #DC2626", fontFamily: GARAMOND }}>Delete</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

    </div>

    {showImport && (
      <ImportModal
        defaultTable={importDefaultTable}
        onClose={() => setShowImport(false)}
        onImport={handleImport}
      />
    )}

    {/* ── Version History Modal ── */}
    {historyTarget && (() => {
      // Merge server versions with any localStorage fallback for this record
      const lsKey = `${historyTarget.type}:${historyTarget.id}`;
      const lsVersions = (versionHistory[lsKey] || []).map(v => ({ snapshot: v.snapshot, saved_by: v.by, created_at: v.at }));
      const serverIds = new Set(fetchedVersions.map(v => v.created_at));
      const merged = [...fetchedVersions, ...lsVersions.filter(v => !serverIds.has(v.created_at))];
      const versions = merged;
      const GARAMOND = FONT_H;
      return (
        <>
          <div onClick={() => setHistoryTarget(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 400 }} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: isMobile ? "min(calc(100vw - 24px), 520px)" : 520, maxHeight: "80vh", background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 12, zIndex: 401, display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "20px 22px 14px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <Clock size={16} color={T.slateBlue} />
                <div style={{ fontSize: 17, fontFamily: GARAMOND, color: T.text, fontWeight: 500 }}>Version History</div>
              </div>
              <div style={{ fontSize: 12, color: T.textMuted, fontFamily: GARAMOND, fontStyle: "italic" }}>"{historyTarget.name}"</div>
            </div>
            <div style={{ overflowY: "auto", flex: 1, padding: "12px 16px" }}>
              {versionsLoading ? (
                <div style={{ textAlign: "center", color: T.textFaint, padding: "32px 0", fontSize: 13, fontFamily: GARAMOND }}>Loading…</div>
              ) : versions.length === 0 ? (
                <div style={{ textAlign: "center", color: T.textFaint, padding: "32px 0", fontSize: 13, fontFamily: GARAMOND }}>No saved versions yet. Versions are saved each time a record is edited.</div>
              ) : versions.map((v, idx) => {
                const date = new Date(v.created_at);
                const label = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                const time  = date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
                const fields = Object.entries(v.snapshot).filter(([k]) => !k.startsWith("_") && k !== "id" && k !== "sn");
                return (
                  <div key={v.id || idx} style={{ borderBottom: `1px solid ${T.border}`, paddingBottom: 14, marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 13, fontFamily: GARAMOND, color: T.text, fontWeight: 500 }}>v{versions.length - idx} · {label} at {time}</div>
                        <div style={{ fontSize: 11, color: T.textMuted, fontFamily: GARAMOND, marginTop: 2 }}>Saved by {v.saved_by}</div>
                      </div>
                      <button
                        onClick={() => {
                          if (historyTarget.type === "active") persistActive(activeGrants.map(g => g.id === historyTarget.id ? v.snapshot : g));
                          else persistApps(applications.map(a => a.id === historyTarget.id ? v.snapshot : a));
                          setHistoryTarget(null);
                        }}
                        style={{ padding: "5px 12px", fontSize: 11, borderRadius: 5, cursor: "pointer", background: T.slateBlue, color: "#fff", border: "none", fontFamily: GARAMOND, flexShrink: 0 }}
                      >Restore</button>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {fields.slice(0, 6).map(([k, val]) => val ? (
                        <span key={k} style={{ fontSize: 10, background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 4, padding: "2px 7px", color: T.textMuted, fontFamily: GARAMOND }}>
                          <span style={{ color: T.textFaint }}>{k}:</span> {String(val).slice(0, 30)}{String(val).length > 30 ? "…" : ""}
                        </span>
                      ) : null)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.border}`, flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setHistoryTarget(null)} style={{ padding: "7px 18px", borderRadius: 6, cursor: "pointer", background: "transparent", color: T.textFaint, border: `1px solid ${T.border}`, fontFamily: GARAMOND, fontSize: 13 }}>Close</button>
            </div>
          </div>
        </>
      );
    })()}

    {/* ── Global floating Move menu ── */}
    {moveMenuId && (
      <div onClick={e => e.stopPropagation()} style={{
        position: "fixed", top: menuPos.top, right: menuPos.right,
        background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 6,
        zIndex: 9999, boxShadow: "0 6px 20px rgba(0,0,0,0.28)", minWidth: 200, overflow: "hidden",
      }}>
        {subTab === "active" && (() => { const g = activeGrants.find(x => x.id === moveMenuId); return g ? <>
          <button onClick={e => { e.stopPropagation(); openEdit(g, "active"); setMoveMenuId(null); }} style={{ display: "block", width: "100%", padding: "10px 16px", fontSize: 12, textAlign: "left", background: "transparent", border: "none", borderBottom: `1px solid ${T.border}`, color: T.textBody, cursor: "pointer", fontFamily: GARAMOND }}>✏️ Edit</button>
          <button onClick={e => { e.stopPropagation(); setDeleteTarget({ id: g.id, type: "active", name: g.shortTitle || g.title }); setMoveMenuId(null); }} style={{ display: "block", width: "100%", padding: "10px 16px", fontSize: 12, textAlign: "left", background: "transparent", border: "none", borderBottom: `1px solid ${T.border}`, color: "#DC2626", cursor: "pointer", fontFamily: GARAMOND }}>🗑️ Delete</button>
          <button onClick={e => { e.stopPropagation(); handleMoveTo(g, "active", "terminated"); }} style={{ display: "block", width: "100%", padding: "10px 16px", fontSize: 12, textAlign: "left", background: "transparent", border: "none", borderBottom: `1px solid ${T.border}`, color: "#b45309", cursor: "pointer", fontFamily: GARAMOND }}>🔴 Move to Terminated</button>
          <button onClick={e => { e.stopPropagation(); handleMoveTo(g, "active", "completed"); }} style={{ display: "block", width: "100%", padding: "10px 16px", fontSize: 12, textAlign: "left", background: "transparent", border: "none", color: "#059669", cursor: "pointer", fontFamily: GARAMOND }}>✅ Move to Completed</button>
        </> : null; })()}
        {subTab === "terminated" && <>
          <button onClick={e => { e.stopPropagation(); const g = terminatedGrants.find(x => x.id === moveMenuId); if (g) handleMoveTo(g, "terminated", "active"); }} style={{ display: "block", width: "100%", padding: "10px 16px", fontSize: 12, textAlign: "left", background: "transparent", border: "none", borderBottom: `1px solid ${T.border}`, color: T.slateBlue, cursor: "pointer", fontFamily: GARAMOND }}>📊 Restore to Active</button>
          <button onClick={e => { e.stopPropagation(); const g = terminatedGrants.find(x => x.id === moveMenuId); if (g) handleMoveTo(g, "terminated", "completed"); }} style={{ display: "block", width: "100%", padding: "10px 16px", fontSize: 12, textAlign: "left", background: "transparent", border: "none", color: "#059669", cursor: "pointer", fontFamily: GARAMOND }}>✅ Move to Completed</button>
        </>}
        {subTab === "completed" && <>
          <button onClick={e => { e.stopPropagation(); const g = completedGrants.find(x => x.id === moveMenuId); if (g) handleMoveTo(g, "completed", "active"); }} style={{ display: "block", width: "100%", padding: "10px 16px", fontSize: 12, textAlign: "left", background: "transparent", border: "none", borderBottom: `1px solid ${T.border}`, color: T.slateBlue, cursor: "pointer", fontFamily: GARAMOND }}>📊 Restore to Active</button>
          <button onClick={e => { e.stopPropagation(); const g = completedGrants.find(x => x.id === moveMenuId); if (g) handleMoveTo(g, "completed", "terminated"); }} style={{ display: "block", width: "100%", padding: "10px 16px", fontSize: 12, textAlign: "left", background: "transparent", border: "none", color: "#DC2626", cursor: "pointer", fontFamily: GARAMOND }}>🔴 Move to Terminated</button>
        </>}
      </div>
    )}

    {/* ── Detail Panel ── */}
    {detailItem && (
      <>
        <div onClick={closeDetail} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200, backdropFilter: "blur(2px)" }} />
        <div style={{ position: "fixed", top: isMobile ? 0 : "50%", left: isMobile ? 0 : "50%", right: isMobile ? 0 : "auto", bottom: isMobile ? 0 : "auto", transform: isMobile ? "none" : "translate(-50%,-50%)", width: isMobile ? "100%" : "min(620px, 92vw)", maxHeight: isMobile ? "100%" : "88vh", background: T.bgSurface, border: isMobile ? "none" : `1px solid ${T.border}`, borderRadius: isMobile ? 0 : 14, zIndex: 201, overflowY: "auto", display: "flex", flexDirection: "column", boxShadow: isMobile ? "none" : "0 24px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)" }}>
          <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, background: T.bgHeader, flexShrink: 0, position: "sticky", top: 0, zIndex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1, paddingRight: 16 }}>
                <div style={{ fontSize: 10, color: T.gold, fontFamily: MONO, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
                  {detailType === "active" ? "Active Grant" : detailType === "terminated" ? "Terminated Grant" : detailType === "completed" ? "Completed Grant" : "Application"}
                </div>
                <h2 style={{ margin: 0, fontSize: 20, fontFamily: GARAMOND, fontWeight: 400, color: T.text, lineHeight: 1.3 }}>
                  {detailType === "active" ? (detailItem.shortTitle || detailItem.title) : detailItem.title}
                </h2>
                <div style={{ fontSize: 13, color: T.slateBlue, fontFamily: GARAMOND, marginTop: 5, fontStyle: "italic" }}>
                  {detailType === "active" ? detailItem.donor : detailItem.funder}
                </div>
              </div>
              <button onClick={closeDetail} title="Close" style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, lineHeight: 1, background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 8, color: T.textFaint, cursor: "pointer", flexShrink: 0 }}>×</button>
            </div>
          </div>
          {detailItem.brief && (
            <div style={{ margin: "16px 24px 0", padding: "14px 18px", background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 8, flexShrink: 0 }}>
              <div style={{ fontSize: 10, color: T.gold, fontFamily: MONO, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>About This Grant</div>
              <div style={{ fontSize: 14, color: T.textBody, fontFamily: GARAMOND, lineHeight: 1.7, fontStyle: "italic" }}>{detailItem.brief}</div>
            </div>
          )}
          <div style={{ padding: "20px 24px", flex: 1 }}>
            {detailType === "active" ? (<>
              <DetailRow label="Donor" value={detailItem.donor} />
              <DetailRow label="Grant Number" value={detailItem.grantNumber} />
              <DetailRow label="Principal Investigator" value={detailItem.pi} />
              <DetailRow label="Contact" value={detailItem.contact} />
              <DetailRow label="Type of Grant" value={detailItem.type} />
              <DetailRow label="States / Location" value={detailItem.states} />
              <DetailRow label="Total Award (USD)" value={detailItem.totalUSD} />
              <DetailRow label="FY26 Funding" value={detailItem.fy26} />
              <DetailRow label="Prime / Sub" value={detailItem.primeOrSub} />
              <DetailRow label="Start Date" value={detailItem.start} />
              <DetailRow label="End Date" value={detailItem.end} />
              <DetailRow label="POC 1" value={detailItem.poc1} />
              {detailItem.poc1Email && <DetailRow label="POC 1 Email" value={<a href={`mailto:${detailItem.poc1Email}`} style={{ color: "#4A6FA5" }}>{detailItem.poc1Email}</a>} />}
              <DetailRow label="POC 2" value={detailItem.poc2} />
              {detailItem.poc2Email && <DetailRow label="POC 2 Email" value={<a href={`mailto:${detailItem.poc2Email}`} style={{ color: "#4A6FA5" }}>{detailItem.poc2Email}</a>} />}
              <DetailRow label="Full Grant Name" value={detailItem.title} />
            </>) : detailType === "app" ? (<>
              <DetailRow label="Funder" value={detailItem.funder} />
              <DetailRow label="RFA Number" value={detailItem.rfaNumber} />
              <DetailRow label="Funding Amount" value={detailItem.amount} />
              <DetailRow label="Award Status" value={detailItem.awardStatus} />
              <DetailRow label="Submission Deadline" value={detailItem.deadline} />
              <DetailRow label="Submitted Date" value={detailItem.submittedDate} />
              <DetailRow label="Application Status" value={detailItem.status} />
              <DetailRow label="Prime / Sub" value={detailItem.primeOrSub} />
              <DetailRow label="Principal Investigator" value={detailItem.pi} />
              <DetailRow label="POC" value={detailItem.poc} />
              <DetailRow label="Remark" value={detailItem.remark} />
            </>) : (<>
              <DetailRow label="Funder" value={detailItem.funder} />
              <DetailRow label="Award Number" value={detailItem.awardNumber} />
              <DetailRow label="Awarded Amount" value={detailItem.awardedAmount} />
              <DetailRow label="Period" value={detailItem.year} />
              <DetailRow label="Prime / Sub" value={detailItem.primeOrSub} />
              <DetailRow label="Principal Investigator" value={detailItem.pi} />
              <DetailRow label="POC 1" value={detailItem.poc1} />
              <DetailRow label="POC 2" value={detailItem.poc2} />
              {detailType === "terminated" && <DetailRow label="Remarks" value={detailItem.remarks} />}
            </>)}
          </div>
          {/* ── Notes ── */}
          <div style={{ padding: "16px 24px", borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, color: T.gold, fontFamily: MONO, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Notes</div>
            {(detailItem._notes || []).length === 0 && (
              <div style={{ fontSize: 12, color: T.textFaint, fontFamily: GARAMOND, marginBottom: 10, fontStyle: "italic" }}>No notes yet. Add one below.</div>
            )}
            {(detailItem._notes || []).map(n => (
              <div key={n.id} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start" }}>
                <div style={{ flex: 1, background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 6, padding: "8px 10px" }}>
                  <div style={{ fontSize: 13, color: T.textBody, fontFamily: GARAMOND, lineHeight: 1.5 }}>{n.text}</div>
                  <div style={{ fontSize: 10, color: T.textFaint, marginTop: 4, fontFamily: MONO }}>{new Date(n.ts).toLocaleString()}</div>
                </div>
                <button onClick={() => deleteNote(n.id)} style={{ background: "transparent", border: "none", color: T.textFaint, cursor: "pointer", fontSize: 15, padding: "4px", lineHeight: 1, flexShrink: 0 }}>×</button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <input value={noteInput} onChange={e => setNoteInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { addNote(noteInput); } }}
                placeholder="Add a note…" style={{ flex: 1, padding: "7px 10px", background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, fontSize: 13, fontFamily: GARAMOND, outline: "none" }} />
              <button onClick={() => addNote(noteInput)} style={{ padding: "7px 14px", background: T.gold, color: "#0A1628", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontFamily: GARAMOND, fontWeight: 600 }}>Add</button>
            </div>
          </div>
          <GrantFiles grantId={detailItem.id} grantName={detailItem.shortTitle || detailItem.title || detailItem.id} />
          {(detailType === "active" || detailType === "app") && (
            <div style={{ padding: "16px 24px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 10, flexShrink: 0, position: "sticky", bottom: 0, background: T.bgSurface }}>
              <button onClick={() => { closeDetail(); openEdit(detailItem, detailType); }} style={{ flex: 1, padding: "9px 0", borderRadius: 6, cursor: "pointer", background: T.gold, color: "#0A1628", border: "none", fontFamily: GARAMOND, fontWeight: 600, fontSize: 13 }}>Edit Record</button>
              <button onClick={() => setDeleteTarget({ id: detailItem.id, type: detailType, name: detailType === "active" ? (detailItem.shortTitle || detailItem.title) : detailItem.title })} style={{ padding: "9px 18px", borderRadius: 6, cursor: "pointer", background: "transparent", color: "#DC2626", border: "1px solid #DC2626", fontFamily: GARAMOND, fontSize: 13 }}>Delete</button>
            </div>
          )}
        </div>
      </>
    )}

    {/* ── Form Modal ── */}
    {editType && (
      <FormCtx.Provider value={{ formData, setFormData }}>
        <div onClick={closeEdit} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300 }} />
        <div style={{ position: "fixed", top: isMobile ? 0 : "50%", left: isMobile ? 0 : "50%", right: isMobile ? 0 : "auto", bottom: isMobile ? 0 : "auto", transform: isMobile ? "none" : "translate(-50%,-50%)", width: isMobile ? "100%" : 640, maxHeight: isMobile ? "100%" : "90vh", background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: isMobile ? 0 : 12, zIndex: 301, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, background: T.bgHeader, flexShrink: 0 }}>
            <h3 style={{ margin: 0, fontFamily: GARAMOND, fontWeight: 400, fontSize: 22, color: T.text }}>
              {editItem?.id ? "Edit" : "Add"} {editType === "active" ? "Active Grant" : "Application"}
            </h3>
          </div>
          <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1 }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? "12px" : "14px 20px" }}>
              {editType === "active" ? (<>
                <FormField label="Donor" k="donor" />
                <FormField label="Grant Number" k="grantNumber" />
                <FormField label="Principal Investigator" k="pi" />
                <FormField label="Co-PI" k="coPi" />
                <FormField label="IHVN Contact" k="contact" span />
                <FormField label="Name of Grants" k="shortTitle" span />
                <FormField label="Short Description of Purpose" k="brief" type="textarea" span />
                <FormField label="Type of Grant" k="type" />
                <FormField label="States of Implementation" k="states" />
                <FormField label="Amount Awarded-NGN" k="amountNGN" />
                <FormField label="Amount Awarded (USD)" k="totalUSD" />
                <FormField label="Prime / Sub" k="primeOrSub" type="select" opts={["Prime","Sub"]} />
                <FormField label="Duration" k="duration" />
                <FormField label="% of Current Funding" k="pctCurrentFunding" />
                <FormField label="Start Date" k="start" type="date" />
                <FormField label="End Date" k="end" type="date" />
                <NOASection />
                <FormField label="GRANTS POC 1" k="poc1" />
                <FormField label="POC 1 Email" k="poc1Email" type="email" />
                <FormField label="GRANTS POC 2" k="poc2" />
                <FormField label="POC 2 Email" k="poc2Email" type="email" />
              </>) : (<>
                <FormField label="Funder" k="funder" />
                <FormField label="RFA Number" k="rfaNumber" />
                <FormField label="Application Title" k="title" span />
                <FormField label="Funding Amount" k="amount" />
                <FormField label="Award Status" k="awardStatus" type="select" opts={["Pending","Awarded","Not Awarded","N/A",""]} />
                <FormField label="Submission Deadline" k="deadline" type="date" />
                <FormField label="Submitted Date" k="submittedDate" type="date" />
                <FormField label="Application Status" k="status" type="select" opts={["Submitted","Not Submitted"]} />
                <FormField label="Prime / Sub" k="primeOrSub" type="select" opts={["Prime","Sub"]} />
                <FormField label="Principal Investigator" k="pi" />
                <FormField label="POC" k="poc" />
                <FormField label="Remark" k="remark" />
                <FormField label="Brief / Notes" k="brief" type="textarea" span />
              </>)}
            </div>
          </div>
          <div style={{ padding: "16px 24px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 10, justifyContent: "flex-end", flexShrink: 0 }}>
            <button onClick={closeEdit} style={{ padding: "9px 20px", borderRadius: 6, cursor: "pointer", background: "transparent", color: T.textFaint, border: `1px solid ${T.border}`, fontFamily: GARAMOND, fontSize: 13 }}>Cancel</button>
            <button onClick={editType === "active" ? handleSaveActive : handleSaveApp} style={{ padding: "9px 24px", borderRadius: 6, cursor: "pointer", background: T.gold, color: "#0A1628", border: "none", fontFamily: GARAMOND, fontWeight: 600, fontSize: 13 }}>Save</button>
          </div>
        </div>
      </FormCtx.Provider>
    )}

    {/* ── Delete Confirm ── */}
    {deleteTarget && (
      <>
        <div onClick={() => setDeleteTarget(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 400 }} />
        <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: isMobile ? "min(calc(100vw - 24px), 400px)" : 400, background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 12, zIndex: 401, padding: "28px 20px 24px" }}>
          <div style={{ fontSize: 18, fontFamily: GARAMOND, color: T.text, marginBottom: 10 }}>Delete this record?</div>
          <div style={{ fontSize: 13, color: T.textFaint, fontFamily: GARAMOND, lineHeight: 1.6, marginBottom: 24 }}>
            <span style={{ color: T.text, fontStyle: "italic" }}>"{deleteTarget.name}"</span> will be moved to the Recycle Bin. It will be permanently deleted after 90 days.
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={() => setDeleteTarget(null)} style={{ padding: "8px 18px", borderRadius: 6, cursor: "pointer", background: "transparent", color: T.textFaint, border: `1px solid ${T.border}`, fontFamily: GARAMOND, fontSize: 13 }}>Cancel</button>
            <button onClick={handleDelete} style={{ padding: "8px 18px", borderRadius: 6, cursor: "pointer", background: "#DC2626", color: "#fff", border: "none", fontFamily: GARAMOND, fontWeight: 600, fontSize: 13 }}>Delete</button>
          </div>
        </div>
      </>
    )}
    </>
  );
}

export default DeadlineDashboard;
